/**
 * TEXT mode (spec §17).
 *
 * Conventional translate interface: source box, target box, swap, copy, speak,
 * and a history that is useful rather than decorative. Translation is debounced
 * at 420 ms with a request-sequence guard, so a fast typist does not queue
 * twenty model calls.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type { LanguageCode, LanguageDetection } from "@shared/types/language";
import type { Transcript } from "@shared/types/transcript";
import { appendHistory, clearHistory, loadHistory, removeHistoryItem, type HistoryItem } from "../lib/storage";
import { Button, IconButton, Panel } from "./ui";
import { IconCopy, IconDownload, IconMute, IconSpeaker, IconSparkle, IconSwap, IconTrash } from "./icons";
import { LanguageBar } from "./LanguageBar";
import { copyToClipboard, downloadTranscript } from "../lib/download";

interface Props {
  sourceLanguage: LanguageCode | "auto";
  targetLanguage: LanguageCode;
  onSourceChange: (value: LanguageCode | "auto") => void;
  onTargetChange: (value: LanguageCode) => void;
  translate: (text: string) => Promise<{ translated: string; detection: LanguageDetection | null }>;
  detect: (text: string) => Promise<LanguageDetection>;
  speak: (text: string, language: LanguageCode) => void;
  cancelSpeech: () => void;
  canSpeak: (language: LanguageCode) => boolean;
  transcript: Transcript;
  historyEnabled: boolean;
  onToast: (title: string, tone: "info" | "success" | "warn" | "error", detail?: string) => void;
}

const DEBOUNCE_MS = 420;
const MAX_CHARS = 4000;

export function TextMode(props: Props) {
  const [source, setSource] = useState("");
  const [target, setTarget] = useState("");
  const [detection, setDetection] = useState<LanguageDetection | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<HistoryItem[]>(() => loadHistory());
  const [showHistory, setShowHistory] = useState(false);

  const sequenceRef = useRef(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const run = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (trimmed === "") {
        setTarget("");
        setDetection(null);
        setError(null);
        return;
      }
      if (trimmed.length > MAX_CHARS) {
        setError(`Text is longer than ${MAX_CHARS} characters. Split it into smaller passages.`);
        return;
      }

      const sequence = ++sequenceRef.current;
      setBusy(true);
      setError(null);
      try {
        const result = await props.translate(trimmed);
        // A slower earlier request must not overwrite a newer result.
        if (sequence !== sequenceRef.current) return;
        setTarget(result.translated);
        setDetection(result.detection);
        if (props.historyEnabled) {
          setHistory(
            appendHistory({
              sourceText: trimmed,
              translatedText: result.translated,
              sourceLanguage: props.sourceLanguage,
              targetLanguage: props.targetLanguage,
              provider: "server",
            }),
          );
        }
      } catch (cause) {
        if (sequence !== sequenceRef.current) return;
        setError(cause instanceof Error ? cause.message : "Translation failed.");
      } finally {
        if (sequence === sequenceRef.current) setBusy(false);
      }
    },
    [props],
  );

  // Debounced auto-translate. Every source change re-arms the timer, so only
  // the last keystroke in a burst reaches the model.
  useEffect(() => {
    if (debounceRef.current !== null) clearTimeout(debounceRef.current);
    if (source.trim() === "") {
      setTarget("");
      return;
    }
    debounceRef.current = setTimeout(() => void run(source), DEBOUNCE_MS);
    return () => {
      if (debounceRef.current !== null) clearTimeout(debounceRef.current);
    };
  }, [run, source]);

  // Re-translate when the language pair changes, but only if there is text.
  useEffect(() => {
    if (source.trim() === "") return;
    void run(source);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.sourceLanguage, props.targetLanguage]);

  const onSwap = (): void => {
    if (props.sourceLanguage === "auto" || target.trim() === "") return;
    props.onSourceChange(props.targetLanguage);
    props.onTargetChange(props.sourceLanguage);
    setSource(target);
    setTarget(source);
  };

  const doDetect = async (): Promise<void> => {
    if (source.trim() === "") return;
    try {
      const result = await props.detect(source);
      setDetection(result);
      if (result.resolved && result.code) {
        props.onSourceChange(result.code);
        props.onToast("Language detected", "success", result.label);
      } else {
        props.onToast("Could not identify the language", "warn", `Heard “${result.rawTag}”, which is not in the language registry.`);
      }
    } catch (cause) {
      props.onToast("Detection failed", "error", cause instanceof Error ? cause.message : undefined);
    }
  };

  const doCopy = async (): Promise<void> => {
    const ok = await copyToClipboard(target || source);
    props.onToast(ok ? "Copied" : "Could not copy", ok ? "success" : "error");
  };

  const doExport = (): void => {
    if (source.trim() === "") return;
    downloadTranscript(
      {
        ...props.transcript,
        entries: props.transcript.entries.length > 0
          ? props.transcript.entries
          : [
              {
                id: "adhoc",
                index: 0,
                timestamp: new Date().toLocaleTimeString([], { hour12: false }),
                startedAt: Date.now(),
                endedAt: Date.now(),
                startMs: 0,
                endMs: 0,
                speaker: "A",
                sourceLanguage: props.sourceLanguage === "auto" ? "en" : props.sourceLanguage,
                sourceText: source,
                targetLanguage: props.targetLanguage,
                translatedText: target,
                detectionConfidence: detection?.confidence ?? null,
                status: "final",
                transcriptionProvider: "typed",
                translationProvider: "server",
                transcriptionMs: 0,
                translationMs: 0,
              },
            ],
      },
      { format: "txt", bilingual: true, includeSpeakers: false, includeHeader: true, targetOnly: false },
    );
  };

  return (
    <div className="stage" style={{ padding: "var(--space-m)", gap: "var(--space-s)", overflowY: "auto" }}>
      <div className="textstack stack">
        <LanguageBar
          layout="stacked"
          source={props.sourceLanguage}
          target={props.targetLanguage}
          onSourceChange={props.onSourceChange}
          onTargetChange={props.onTargetChange}
          onSwap={onSwap}
          detectedLabel={detection?.label ?? null}
        />
        <Panel
          title={
            <>
              Original
              {detection?.resolved ? (
                <span className="badge badge--accent" title={`Detected with ${Math.round(detection.confidence * 100)}% confidence`}>
                  <IconSparkle size={11} /> {detection.label}
                </span>
              ) : null}
            </>
          }
          actions={
            <>
              <Button size="sm" onClick={() => void doDetect()} disabled={source.trim() === "" || busy}>
                Detect
              </Button>
              <IconButton label="Copy the original" onClick={() => void copyToClipboard(source)} disabled={source.trim() === ""}>
                <IconCopy size={15} />
              </IconButton>
            </>
          }
        >
          <div className="panel__body stack">
            <textarea
              className="textarea"
              value={source}
              onChange={(event) => setSource(event.target.value.slice(0, MAX_CHARS))}
              placeholder="Type or paste text in any language…"
              dir="auto"
              rows={7}
              spellCheck={false}
              aria-label="Source text"
            />
            <div className="row-between">
              <span className="dim mono">{source.length} / {MAX_CHARS}</span>
              <Button size="sm" onClick={() => { setSource(""); setTarget(""); setDetection(null); }} disabled={source === ""}>
                Clear
              </Button>
            </div>
          </div>
        </Panel>

        <Panel
          title="Translation"
          actions={
            <>
              <IconButton
                label="Swap languages"
                onClick={onSwap}
                disabled={props.sourceLanguage === "auto" || target === ""}
              >
                <IconSwap size={15} />
              </IconButton>
              <IconButton label="Speak the translation" onClick={() => props.speak(target, props.targetLanguage)} disabled={target === "" || !props.canSpeak(props.targetLanguage)}>
                <IconSpeaker size={15} />
              </IconButton>
              <IconButton label="Stop speaking" onClick={props.cancelSpeech}>
                <IconMute size={15} />
              </IconButton>
              <IconButton label="Copy the translation" onClick={() => void doCopy()} disabled={target === ""}>
                <IconCopy size={15} />
              </IconButton>
              <IconButton label="Download" onClick={doExport} disabled={source.trim() === ""}>
                <IconDownload size={15} />
              </IconButton>
            </>
          }
        >
          <div className="panel__body stack">
            <div
              className="textarea"
              dir="auto"
              style={{ display: "flex", alignItems: "flex-start", minHeight: 148, whiteSpace: "pre-wrap", overflowWrap: "anywhere", lineHeight: 1.6 }}
              aria-live="polite"
            >
              {busy && target === "" ? <span className="dim">Translating…</span> : target || <span className="dim">The translation appears here.</span>}
            </div>
            {error ? <div className="badge badge--warn">{error}</div> : null}
          </div>
        </Panel>
      </div>

      <Panel
        title="History"
        actions={
          <>
            <Button size="sm" onClick={() => setShowHistory((current) => !current)}>
              {showHistory ? "Hide" : "Show"} ({history.length})
            </Button>
            <Button
              size="sm"
              variant="ghost"
              iconOnly
              onClick={() => {
                clearHistory();
                setHistory([]);
              }}
              disabled={history.length === 0}
              title="Clear history"
              aria-label="Clear history"
            >
              <IconTrash size={15} />
            </Button>
          </>
        }
      >
        {showHistory ? (
          <div className="scroller" style={{ maxHeight: 260 }}>
            {history.length === 0 ? (
              <div className="stream__empty" style={{ padding: "var(--space-m)" }}>
                <span className="dim">Nothing yet.</span>
              </div>
            ) : (
              <div className="entries">
                {history.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className="entry"
                    onClick={() => {
                      setSource(item.sourceText);
                      setTarget(item.translatedText);
                    }}
                    style={{ textAlign: "left", width: "100%" }}
                  >
                    <span className="entry__time tabular">
                      {new Date(item.at).toLocaleTimeString([], { hour12: false, hour: "2-digit", minute: "2-digit" })}
                    </span>
                    <span style={{ minWidth: 0 }}>
                      <span className="entry__source truncate" style={{ display: "block" }}>{item.sourceText}</span>
                      <span className="entry__target truncate" style={{ display: "block" }}>{item.translatedText}</span>
                    </span>
                    <span
                      role="button"
                      tabIndex={0}
                      className="dim"
                      onClick={(event) => {
                        event.stopPropagation();
                        setHistory(removeHistoryItem(item.id));
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.stopPropagation();
                          setHistory(removeHistoryItem(item.id));
                        }
                      }}
                      title="Remove"
                    >
                      <IconTrash size={13} />
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : null}
      </Panel>
    </div>
  );
}
